// Web push for this browser: the site on an iPhone's Home Screen (iOS 16.4+)
// or in a desktop browser becomes one of the signed-in user's devices on the
// notification pipeline, next to the Android app. Turning it on subscribes
// the browser with the server's VAPID key (web-push edge function) and
// registers the subscription (register_web_push_subscription) for the
// signed-in account, which the browser remembers; turning it off, and
// signing out (createAuthStore), removes the registration and the
// subscription. The service worker (public/sw.js) shows what the server
// sends. The decisions live in createWebPush.ts; this binds them to this
// browser and Supabase.
//
// A replaced subscription (pushsubscriptionchange) is registered from the
// page, not the worker: the worker holds no session (the session lives in
// the page's storage, and copying it into the worker would be a second
// place for a credential). The worker subscribes again and posts a message;
// a page that is open registers at once, and otherwise the next page load
// with a session does (sync below), before the old endpoint's 410 can cost
// the device its row and before anything is due to it.

import { createWebPush, deviceLabel, type PushBrowser, type PushServer, type Registration, type WebPush } from './createWebPush';
import { invokeEdgeFunction } from './edgeFunctions';
import { errorMessage, isRecord } from './errors';
import { supabase, auth, getSession } from './supabase';

const REGISTRATION = 'web-push-registration';

const isRegistration = (value: unknown): value is Registration =>
  isRecord(value) && typeof value.endpoint === 'string' && value.endpoint !== '' && typeof value.userId === 'string' && value.userId !== '';

function thisBrowser(): PushBrowser {
  const userAgent = navigator.userAgent;
  // iPadOS reports itself as a Mac; a Mac has no touch points.
  const ios = /iPhone|iPad|iPod/.test(userAgent) || (/Mac OS X/.test(userAgent) && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  let registration: Promise<ServiceWorkerRegistration> | null = null;
  return {
    supported,
    standalone,
    ios,
    label: deviceLabel(userAgent, ios, standalone),
    permission: () => Notification.permission,
    requestPermission: () => Notification.requestPermission(),
    registration: () => {
      registration ??= navigator.serviceWorker.register('/sw.js').then(() => navigator.serviceWorker.ready);
      return registration;
    },
    remembered: () => {
      // A browser that keeps no site data, or anything but a registration under the key, remembers no registration.
      try {
        const kept: unknown = JSON.parse(localStorage.getItem(REGISTRATION) ?? 'null');
        return isRegistration(kept) ? kept : null;
      } catch {
        return null;
      }
    },
    remember: (kept) => {
      try {
        if (kept) localStorage.setItem(REGISTRATION, JSON.stringify(kept));
        else localStorage.removeItem(REGISTRATION);
      } catch (error) {
        throw new Error(`this browser keeps no site data, so it cannot be kept in step with your notifications (${errorMessage(error)})`);
      }
    },
  };
}

const supabaseServer: PushServer = {
  async user() {
    const { data } = await getSession();
    return data.session?.user.id ?? null;
  },
  async publicKey() {
    const answer = await invokeEdgeFunction('web-push', { action: 'public_key' });
    const key = answer && typeof answer === 'object' ? (answer as { public_key?: unknown }).public_key : undefined;
    if (typeof key !== 'string' || !key) throw new Error('the server did not hand over its notification key');
    return key;
  },
  async register(keys, label, replacesEndpoint) {
    const { data, error } = await supabase.rpc('register_web_push_subscription', {
      p_endpoint: keys.endpoint,
      p_p256dh: keys.p256dh,
      p_auth: keys.auth,
      p_device_label: label,
      p_replaces_endpoint: replacesEndpoint,
    });
    if (error) throw error;
    if (typeof data !== 'string') throw new Error('register_web_push_subscription returned no device id');
    return data;
  },
  async unregister(endpoint) {
    const { data, error } = await supabase.rpc('unregister_web_push_subscription', { p_endpoint: endpoint });
    if (error) throw error;
    return data === true;
  },
};

/** Web push for this browser and the signed-in user. */
export const webPush: WebPush = createWebPush(thisBrowser(), supabaseServer);

/** Queue a test notification to every device on the account (this one among them); returns how many. At most three per ten minutes. */
export async function sendTestNotification(): Promise<number> {
  const { data, error } = await supabase.rpc('queue_test_notification');
  if (error) throw error;
  if (typeof data !== 'number') throw new Error('queue_test_notification returned no device count');
  return data;
}

const browserSupportsPush = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Keep the server's registration in step with the subscription this browser holds; a failure is logged, and Settings shows it. */
function syncRegistration(): void {
  webPush.sync().catch((error: unknown) => console.error('Web push registration was not refreshed:', error));
}

if (browserSupportsPush) {
  // The service worker subscribed again after the push service replaced the subscription.
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent<{ type?: unknown }>) => {
    if (event.data?.type === 'pushsubscriptionchange') syncRegistration();
  });
  // Once for each account signed in on this page load, on the first event
  // carrying their session (the page loading signed in, or a sign-in on it):
  // a subscription replaced while no page was open is registered here. An
  // event without a session (a signed-out page load) is not the sign-in
  // that may follow it, and syncs nothing.
  let syncedFor: string | null = null;
  auth.onAuthStateChange((event, session) => {
    if (!session || (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN') || session.user.id === syncedFor) return;
    syncedFor = session.user.id;
    syncRegistration();
  });
}
